import * as React from 'react';
import { ArrowLeftCircle, ArrowRightCircle, Folder } from 'react-feather';
import { format } from 'date-fns';

import { Member, KeyHolder, SortMethod } from '../MemberRepository';
import KMSUsecase from '../KMSUsecase';
import { KeyHolderState, MemberState } from '../KMSUsecase';

type HolderItemProps = {
  holder: KeyHolder;
  onClick(m: Member): void;
}
function HolderItem(props: HolderItemProps) {
  const member = props.holder.member;
  const since = props.holder.since;
  function handleClick(e) {
    props.onClick(member);
  }
  return (
    <li className="mx-sm-4">
      <div className="text-center">
        <button className="btn btn-primary rounded-0" onClick={handleClick}>{member.name}</button>
      </div>
      <div className="text-center">
        <small className="">{format(since, "MM/dd HH:mm")}</small>
      </div>
    </li>
  );
}

function UnHolderItem(props) {
  return (
    <li className="mx-sm-4">
      <div className="text-center">
        <button className="btn btn-light rounded-0">空き</button>
      </div>
      <div>&nbsp;</div>
    </li>
  );
}

type HolderListProps = {
  keyHolders: KeyHolder[];
  handleHolderClick(m: Member): void;
}
function HolderList(prop: HolderListProps) {
  const KEY_NUM = 3;

  const holderItems = prop.keyHolders.map((h) => {
    return <HolderItem key={h.member.id} holder={h} onClick={prop.handleHolderClick} />
  });
  for (let i = holderItems.length; i < KEY_NUM; i++) {
    holderItems.push(<UnHolderItem key={-i} />)
  }

  return (
    <div className="card">
      <div className="card-body">
        <ul className="d-flex justify-content-between justify-content-sm-center list-unstyled mb-0">
          {holderItems}
        </ul>
      </div>
    </div>
  )
}

type MemberItemProps = {
  member: Member;
  onClick(m: Member): void;
}
function MemberItem(props: MemberItemProps) {
  const member = props.member;
  function handleClick(e) {
    props.onClick(member);
  }
  return (
    <button className="btn btn-info rounded-pill mx-2 my-1" onClick={handleClick}>{member.name}</button>
  );
}

type MemberAreaProps = MemberState & {
  handleMemberClick(m: Member): void;
  handleChangeMemberSortMethod(ev: React.ChangeEvent<HTMLSelectElement>): void;
}
function MemberArea(props: MemberAreaProps) {
  const memberInOffice = props.memberInOffice.map((m) => {
    return <MemberItem key={m.id} member={m} onClick={props.handleMemberClick} />
  });
  const memberOutOffice = props.memberOutOffice.map((m) => {
    return <MemberItem key={m.id} member={m} onClick={props.handleMemberClick} />
  });
  return (
    <div>
      <div className="row">
        <div className="col-8 col-sm-3 mx-auto">
          <select className="form-control rounded-pill" value={props.memberSortMethod} onChange={props.handleChangeMemberSortMethod}>
            <option value="id_asc">登録順</option>
            <option value="yomi_asc">あいうえお順</option>
          </select>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-lg-8 offset-lg-2 d-inline-flex flex-wrap justify-content-center">
          {memberInOffice}
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-lg-8 offset-lg-2 d-inline-flex flex-wrap justify-content-center">
          {memberOutOffice}
        </div>
      </div>
    </div>
  )
}

function LoadingSpinner(props) {
  return (
    <div className="row">
      <div className="col-12 d-flex justify-content-center">
        <div className="spinner-border mr-2" role="status" aria-hidden="true"></div>
        <strong>Loading...</strong>
      </div>
    </div>
  )
}

interface AppState extends KeyHolderState, MemberState {
  isMemberLoading: boolean;
}

export default class App extends React.Component<{}, AppState> {
  readonly usecase: KMSUsecase;

  constructor(props) {
    super(props);
    this.usecase = new KMSUsecase();
    // ハンドラ初期化
    this.handleMemberClick = this.handleMemberClick.bind(this);
    this.handleHolderClick = this.handleHolderClick.bind(this);
    this.handleUndoKeyholder = this.handleUndoKeyholder.bind(this);
    this.handleRedoKeyholder = this.handleRedoKeyholder.bind(this);
    this.handleChangeMemberSortMethod = this.handleChangeMemberSortMethod.bind(this);
    // state初期化
    this.state = {
      keyHolders: [],
      canUndo: false,
      canRedo: false,

      memberInOffice: [],
      memberOutOffice: [],
      memberSortMethod: SortMethod.ID_ASC,

      isMemberLoading: false,
    };
  }

  componentDidMount() {
    this.usecase.getKeyHolders().then((newState: KeyHolderState) => {
      this.setState(newState);
    });

    this.setState({
      isMemberLoading: true
    });
    this.usecase.getMembers().then((newState: MemberState) => {
      this.setState(newState);
    }).finally(() => {
      this.setState({
        isMemberLoading: false
      });
    })
  }

  handleHolderClick(member: Member) {
    const newState: KeyHolderState = this.usecase.unbindKeyHolder(member);
    this.setState(newState);
  }

  handleMemberClick(member: Member) {
    const newState: KeyHolderState = this.usecase.bindKeyHolder(member);
    if (newState) {
      this.setState(newState);
    }
  }

  handleUndoKeyholder(e) {
    const newState: KeyHolderState = this.usecase.undoKeyHolders();
    this.setState(newState);
  }

  handleRedoKeyholder(e) {
    const newState: KeyHolderState = this.usecase.redoKeyHolders();
    this.setState(newState);
  }

  handleChangeMemberSortMethod(ev: React.ChangeEvent<HTMLSelectElement>) {
    const newSortMethod = ev.target.value as SortMethod;
    const newState: MemberState = this.usecase.changeMembersSort(newSortMethod);
    this.setState(newState);
  }

  render() {
    return (
      <>
        <div className="row pt-3">
          <h1 className="col d-flex justify-content-center">
            <div className={"mr-5" + " " + (!this.state.canUndo && "invisible")}>
              <ArrowLeftCircle onClick={this.handleUndoKeyholder} />
            </div>
            <div className="text-center">
              rKMS(Maison) <span className="d-none d-lg-inline">Dashboard</span>
            </div>
            <div className={"ml-5" + " " + (!this.state.canRedo && "invisible")}>
              <ArrowRightCircle onClick={this.handleRedoKeyholder} />
            </div>
          </h1>
        </div>
        <div className="row mb-2">
          <div className="col-lg-8 offset-lg-2">
            <HolderList keyHolders={this.state.keyHolders} handleHolderClick={this.handleHolderClick} />
          </div>
        </div>
        {this.state.isMemberLoading ? (
          <LoadingSpinner />
        ) : (
          <MemberArea
            memberInOffice={this.state.memberInOffice}
            memberOutOffice={this.state.memberOutOffice}
            handleMemberClick={this.handleMemberClick}
            memberSortMethod={this.state.memberSortMethod}
            handleChangeMemberSortMethod={this.handleChangeMemberSortMethod} />
        )
        }
      </>
    )
  }
}